// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Shoving the cabinet with your own hands (M5-10, M5-12, XR-006).
//
// "Nudging with your own body is the thing a flat screen fundamentally cannot do, and it is why
// the headset version is worth a subscription" (06 §2.3). All of which rests on four rules, and
// this file is those four rules with nothing else in it:
//
// 1. **Contact.** The hand has to be touching the cabinet's outer shell.
// 2. **Moving into it.** A hand *resting* on the cabinet does nothing, forever. This is the rule
//    that makes the feature usable at all: a player's palms live on the side rails between
//    shots, and a contact test alone would nudge continuously for the whole game.
// 3. **Scaled and clamped.** The impulse goes with hand speed, up to a hard shove. A wild swing
//    cannot exceed what a person could actually do to 120 kg of plywood.
// 4. **One shove per contact.** A hand that stays in contact for 300 ms delivered one push, not
//    twenty-two of them. Re-arming needs the hand to leave, which is what a real second shove
//    involves.
//
// The impulse is in newton-seconds against the machine frame, the same unit the keyboard nudge
// uses (`NUDGE_IMPULSE` in `input/desktop.ts`), because it goes to the same place — `World.nudge`,
// PH-070. There is no XR-specific nudge path in the simulation and there must not be: the tilt
// bob has to see a shove from a hand exactly as it sees a shove from a key, or a headset player
// and a desktop player are playing two different machines.

/** A shove, in newton-seconds in the playfield plane. */
export interface NudgeImpulse {
  readonly ix: number
  readonly iy: number
  /** True when this was hard enough to be a slam (06 §2.3, FR-007). */
  readonly slam: boolean
  /** Contact speed, m/s — the sound's gain and the cabinet's visible displacement. */
  readonly speed: number
}

/**
 * Hand speed below which a contact is not a shove, m/s.
 *
 * 0.25 m/s. A hand resting on a rail drifts at a few centimetres a second, and a hand adjusting
 * its grip moves at maybe 0.1; a deliberate shove starts around half a metre a second. The gap
 * is wide, which is why this can be a single threshold rather than another hysteresis.
 */
export const NUDGE_MIN_SPEED = 0.25

/**
 * Is this hand moving fast enough to be shoving the machine rather than carrying it?
 *
 * **One predicate, because a single hand must never be both** (M8-40). The shove path has asked
 * this since M5 as an inline comparison against `NUDGE_MIN_SPEED`, and `cabinetGrab` never asked
 * it at all. A Quest 3 measured the cost on 1 Sep 2026 — the two clients share this file's shape,
 * so the browser had it too — with fourteen nudges and four carries in one 31-second window and
 * **three of the four carries beginning 29 ms, 27 ms and 152 ms after a nudge**. A carry rewrites
 * the player's stored placement on release, so the machine arrived somewhere new on the next
 * recentre. Reported as "the location reset during play".
 *
 * **`cabinetGrab`'s header says the discriminator is the fist** — "an open hand shoves the
 * machine, a closed one carries it" — and that cannot be implemented as written: hand tracking is
 * least certain about whether a hand is open exactly when it is moving quickly, which is the case
 * this has to decide. Speed comes straight off the joint pose and is not a classification.
 *
 * Both callers read this, so there is no band in which one hand does both.
 */
export function nudging(speed: number): boolean {
  return speed >= NUDGE_MIN_SPEED
}

/**
 * Hand speed that produces the hardest legal shove, m/s.
 *
 * 1.6 m/s is a firm two-handed push. Above it the impulse is clamped, so the physical limit on
 * how hard a player can nudge is the clamp and not their reach — which is what stops a headset
 * player from having an advantage a desktop player cannot match, and stops the tilt bob from
 * being the only thing between the game and a player who flails.
 */
export const NUDGE_FULL_SPEED = 1.6

/**
 * The impulse a full-speed shove delivers, newton-seconds.
 *
 * The keyboard's `NUDGE_IMPULSE` is 26 for one tap. A hand shove tops out slightly above it at
 * 34, because a real one is harder than a key press and because a player who has physically
 * pushed a cabinet and seen the ball barely move will conclude the feature does not work. The
 * tilt bob is what stops this being free, exactly as it does on a real machine.
 */
export const NUDGE_FULL_IMPULSE = 34

/**
 * Contact speed that counts as a slam, m/s.
 *
 * 3.2 m/s — twice the hardest playable shove, which is not a nudge by any reading. A real slam
 * tilt switch is a weight on a spring that only a genuine blow to the cabinet closes, and the
 * threshold here is set so that no amount of enthusiastic play reaches it and a deliberate
 * punch always does.
 */
export const SLAM_SPEED = 3.2

/** How far outside the shell still counts as touching it, metres. Hand tracking is not exact. */
export const CONTACT_SKIN = 0.04

/** The cabinet's outer shell, in cabinet-local metres. */
export interface ShellBox {
  readonly halfWidth: number
  readonly halfLength: number
  readonly top: number
}

/** Which face a hand is against, or none. */
export type ShellFace = 'left' | 'right' | 'front' | 'back' | 'top' | undefined

/**
 * Which face of the shell a point is touching.
 *
 * Nearest-face rather than first-match, so a hand at a corner is attributed to the face it is
 * actually closest to. A first-match test attributes every corner contact to whichever face the
 * `if` chain happens to check first, which puts a diagonal shove on the wrong axis.
 */
export function shellContact(box: ShellBox, x: number, y: number, z: number): ShellFace {
  if (y > box.top + CONTACT_SKIN || y < -CONTACT_SKIN) return undefined
  const dLeft = Math.abs(x + box.halfWidth)
  const dRight = Math.abs(x - box.halfWidth)
  const dFront = Math.abs(z - box.halfLength)
  const dBack = Math.abs(z + box.halfLength)
  const dTop = Math.abs(y - box.top)

  // Outside the footprint by more than the skin on either axis: not touching anything.
  const insideX = x > -box.halfWidth - CONTACT_SKIN && x < box.halfWidth + CONTACT_SKIN
  const insideZ = z > -box.halfLength - CONTACT_SKIN && z < box.halfLength + CONTACT_SKIN
  if (!insideX || !insideZ) return undefined

  let best: ShellFace
  let bestDistance = CONTACT_SKIN
  const consider = (d: number, face: Exclude<ShellFace, undefined>): void => {
    if (d <= bestDistance) {
      bestDistance = d
      best = face
    }
  }
  consider(dLeft, 'left')
  consider(dRight, 'right')
  consider(dFront, 'front')
  consider(dBack, 'back')
  consider(dTop, 'top')
  return best
}

/** The inward normal of a face, in cabinet space. */
function inwardOf(face: Exclude<ShellFace, undefined>): { x: number; z: number } {
  switch (face) {
    case 'left':
      return { x: 1, z: 0 }
    case 'right':
      return { x: -1, z: 0 }
    case 'front':
      return { x: 0, z: -1 }
    case 'back':
      return { x: 0, z: 1 }
    case 'top':
      // A push down on the lockdown bar shoves the machine away from the player, which is what
      // happens when you lean on one. It is the weakest of the five and it is not nothing.
      return { x: 0, z: -1 }
  }
}

/**
 * One hand's contact with the shell, frame by frame.
 *
 * One of these per hand. It holds exactly one bit of state — whether this contact has already
 * delivered its shove — which is rule 4 above and is the difference between a nudge and a
 * vibration.
 */
export class ShoveDetector {
  private armed = true

  /**
   * Feed a hand position and its filtered velocity in **cabinet space**.
   *
   * Cabinet space and not world space, because the cabinet may be at any scale, position and
   * rotation in MR (06 §3) and the shell test is stated in cabinet-local metres. The caller
   * transforms; the transform is the caller's because only the caller knows the placement.
   */
  advance(
    box: ShellBox,
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
  ): NudgeImpulse | undefined {
    const face = shellContact(box, x, y, z)
    if (face === undefined) {
      this.armed = true
      return undefined
    }
    if (!this.armed) return undefined

    const speed = Math.sqrt(vx * vx + vy * vy + vz * vz)
    if (speed < NUDGE_MIN_SPEED) return undefined

    // Rule 2: only the component moving *into* the face counts. A hand sliding along the rail at
    // a metre a second has an enormous speed and no inward component at all.
    const inward = inwardOf(face)
    const into = vx * inward.x + vz * inward.z + (face === 'top' ? -vy : 0)
    if (into < NUDGE_MIN_SPEED) return undefined

    this.armed = false

    const t = into >= NUDGE_FULL_SPEED ? 1 : into / NUDGE_FULL_SPEED
    const magnitude = NUDGE_FULL_IMPULSE * t
    return {
      // Playfield plane: cabinet +X is the playfield's +X, and cabinet −Z (into the machine) is
      // the playfield's +Y, because the playfield's up-field direction points away from the
      // player. Getting this pair the wrong way round produces a machine that nudges sideways
      // when shoved from the front, which is the single most obvious possible bug in the feature.
      ix: inward.x * magnitude,
      iy: -inward.z * magnitude,
      slam: into >= SLAM_SPEED,
      speed: into,
    }
  }

  /** Forget the contact — used when a hand stops being tracked. */
  reset(): void {
    this.armed = true
  }
}
